/**
 * Full-screen states: title, pause, shift report, victory and defeat.
 *
 * One container, one screen visible at a time. These are the only places the game stops,
 * so each one exists to answer a single question — what happened, and what now.
 */

import { CREDITS } from '../content/credits';
import type { Resources, ShiftProgress } from '../sim/types';
import { MANIFEST_ITEMS, UPGRADES, type UpgradeId } from '../content/economy';
import { SHIFTS } from '../content/shifts';

export interface ScreenCallbacks {
  onNewCampaign: () => void;
  onContinue: () => void;
  onNextShift: () => void;
  /** Start Endless mode, unlocked by finishing the campaign. */
  onEndless: () => void;
  onRetryShift: () => void;
  onResume: () => void;
  onAbandon: () => void;
  /** Move to the requisition screen from the shift report. */
  onRequisition: () => void;
  /** Buy an upgrade. Returns true if the purchase went through. */
  onBuyUpgrade: (id: UpgradeId) => boolean;
  /** Order or cancel a manifest item. Returns the new pending count for that item. */
  onOrderManifest: (id: string, delta: number) => number;
  onSetVolume: (v: number) => void;
  onSetMuted: (muted: boolean) => void;
  onSetInvert: (view: 'scope' | 'horizon', inverted: boolean) => void;
}

export interface RequisitionView {
  resources: Resources;
  upgrades: readonly UpgradeId[];
  pendingManifest: readonly string[];
  nextShift: number;
}

/**
 * How long before the film ends its fade to black begins, seconds.
 *
 * The fade used to hang off the `ended` event, which fires only once playback has
 * stopped — so the last frame sat frozen on screen for the whole fade. Starting it this
 * far out means the picture is already going dark while it is still moving, and reaches
 * black as the film runs out. The CSS transition is driven from the same number (see
 * `--intro-fade`) so the two cannot drift apart.
 */
const INTRO_FADE_LEAD = 1;

/**
 * Which cut of the intro film this screen gets.
 *
 * The footage exists twice — 736×400 for a landscape screen and 400×736 for a phone held
 * upright — because the element is `object-fit: cover`, and covering a tall viewport with
 * the wide cut crops it to a narrow strip of its own centre. A film shot for the horizon
 * survives that badly.
 *
 * Orientation is part of the test rather than the pointer alone: a phone turned sideways
 * has a landscape screen and wants the landscape cut. Read once, at construction, because
 * the element carries a single source for the life of the page — swapping it mid-film
 * would restart the helicopter ride from frame zero, which is worse than a crop.
 */
function introFilmSrc(): string {
  const portraitPhone =
    window.matchMedia?.('(pointer: coarse) and (orientation: portrait)').matches ?? false;
  return portraitPhone ? '/video/intro-mobile.mp4' : '/video/intro.mp4';
}

export class Screens {
  private readonly root: HTMLDivElement;
  private readonly cb: ScreenCallbacks;
  private readonly video: HTMLVideoElement;
  private soundUnlocked = false;
  /**
   * The film is a one-time event, not a backdrop.
   *
   * Once it has run it fades to black and stays there — including if the player comes
   * back to the title between shifts. Everything that might otherwise restart it (the
   * sound-unlock gesture, re-showing the title) checks this first.
   */
  private videoFinished = false;
  /**
   * Set by the enter gate, consumed by the next `showTitle`.
   *
   * The film should have the screen to itself for a moment before the menu arrives, so
   * the first title after the gesture fades its panel in late (see `.screen.entrance`).
   * Returning to the title later — between shifts, after a defeat — is navigation, not
   * an opening, and must be instant.
   */
  private titleEntrancePending = false;
  /** Object URL for the preloaded film, kept only so it can be revoked on dispose. */
  private filmUrl: string | null = null;

  constructor(parent: HTMLElement, callbacks: ScreenCallbacks) {
    this.cb = callbacks;
    this.root = document.createElement('div');
    this.root.id = 'screens';

    // Title-screen backdrop. It sits *inside* the overlay container, beneath the dark
    // scrim and the buttons, so the menu reads over it rather than beside it.
    this.video = document.createElement('video');
    this.video.id = 'titleVideo';
    /*
     * No source yet. `loadIntroFilm` assigns one — see there for why the boot sequence
     * owns this — and `setVideoActive` assigns a plain URL as a fallback for the paths
     * that never boot through the gate (`?play`, `?fly`, a benchmark that later reaches
     * the title). Assigning here as well would mean fetching the film twice.
     */
    // Plays once, on Adam's direction. It ends on black and the menu stays over it.
    this.video.loop = false;
    /*
     * No autoplay. The film is gated behind the enter screen so it can start from frame
     * zero WITH sound: every browser blocks audio until a real gesture, and the old
     * behaviour — autoplay muted, unmute on whatever the player happened to touch —
     * meant the helicopter ride played silently and the audio cut in partway through,
     * or never, if their first click was the one that started the campaign.
     */
    this.video.autoplay = false;
    this.video.playsInline = true;
    this.video.preload = 'auto';
    // Every browser blocks autoplay with audio until the user has interacted with the
    // page, and a rejected play() would leave a black rectangle. So it starts muted and
    // unmutes itself on the first gesture — which on the title screen is imminent anyway.
    this.video.muted = true;
    /*
     * Fade by brightness rather than opacity. The overlay container is transparent —
     * the blurred live scene sits behind it — so fading the element out would reveal
     * the rig rather than going black. Driving brightness to zero leaves the element
     * fully opaque and genuinely black, which is what "fade to black and stay black"
     * has to mean here.
     */
    this.video.style.setProperty('--intro-fade', `${INTRO_FADE_LEAD}s`);
    /*
     * Begin the fade a second before the end rather than after it. `timeupdate` fires
     * about four times a second, which is ample for a one-second cue and far more
     * robust than a timer, which would drift on any stall or seek.
     */
    this.video.addEventListener('timeupdate', () => {
      const { duration, currentTime } = this.video;
      if (!Number.isFinite(duration) || duration <= 0) return;
      if (currentTime < duration - INTRO_FADE_LEAD) return;
      this.beginIntroFade();
    });
    // Backstop: a film short enough that the cue never lands still has to go dark.
    this.video.addEventListener('ended', () => this.beginIntroFade());
    this.root.appendChild(this.video);

    parent.appendChild(this.root);
    this.installSoundUnlock();
  }

  /**
   * Pull the entire film down before anything is allowed to show it.
   *
   * `preload="auto"` is a hint, not an instruction: the browser stops at whatever it
   * decides is enough, and on a cold first load that meant the gate opened, the player
   * clicked, and the helicopter ride sat frozen on frame one for several seconds while
   * the rest arrived. Adam's requirement is that this cannot happen — so the bytes are
   * fetched here, in full, as part of the boot sequence, and the boot overlay stays up
   * until this resolves.
   *
   * Fetched into a Blob rather than pointed at with a URL because that is the only way to
   * *know* the file is in hand; `canplaythrough` is the browser's estimate of whether it
   * can keep ahead of playback, which is a weaker promise and one Safari is erratic about
   * making at all. The request still goes through the service worker, so it is cached on
   * the way past exactly as an element-issued one would be.
   *
   * Never rejects. A film that cannot be fetched is a degraded title screen; a boot
   * sequence that hangs on it is no game at all.
   */
  async loadIntroFilm(onProgress?: (fraction: number) => void): Promise<void> {
    const src = introFilmSrc();
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const total = Number(res.headers.get('content-length')) || 0;
      const reader = res.body?.getReader();
      let blob: Blob;
      if (reader) {
        const chunks: Uint8Array[] = [];
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          // Only report against a known length — a chunk count is not a fraction.
          if (total > 0) onProgress?.(Math.min(1, received / total));
        }
        blob = new Blob(chunks as BlobPart[], { type: res.headers.get('content-type') ?? 'video/mp4' });
      } else {
        // No streaming body (an old Safari, or a cache hit served whole).
        blob = await res.blob();
      }
      this.filmUrl = URL.createObjectURL(blob);
      this.video.src = this.filmUrl;
    } catch (err) {
      console.warn('[deepwater] intro film could not be preloaded; streaming it instead', err);
      this.video.src = src;
    }
    onProgress?.(1);
    await this.whenFilmPlayable();
  }

  /**
   * Wait for the decoder to catch up with the bytes.
   *
   * Backstopped rather than open-ended: with the whole file already local, a readyState
   * that has not arrived within a few seconds means a decode problem that more waiting
   * will not solve, and hanging the boot on it would turn a soft failure into a hard one.
   */
  private whenFilmPlayable(): Promise<void> {
    const READY_TIMEOUT_MS = 8000;
    if (this.video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const done = (): void => {
        window.clearTimeout(timer);
        this.video.removeEventListener('canplaythrough', done);
        this.video.removeEventListener('error', done);
        resolve();
      };
      const timer = window.setTimeout(() => {
        console.warn('[deepwater] intro film never reported ready; opening the gate anyway');
        done();
      }, READY_TIMEOUT_MS);
      this.video.addEventListener('canplaythrough', done);
      this.video.addEventListener('error', done);
      // No `load()` call: assigning `src` already invoked the media load algorithm, and
      // calling it again would restart the buffering we are waiting on.
    });
  }

  /** Start the run to black. Idempotent — the cue fires on every timeupdate. */
  private beginIntroFade(): void {
    if (this.videoFinished) return;
    this.videoFinished = true;
    this.video.classList.add('ended');
  }

  /**
   * Turn the audio on at the first user gesture, then stop listening.
   *
   * This unmutes and nothing else. It used to start the film too, and because it
   * listens for POINTERDOWN — which fires before click — pressing the enter button
   * began playback while the gate was still up and the video still hidden. By the time
   * the gate dismissed, the helicopter ride was already seconds in. The gate is now the
   * only thing that starts the film; this exists solely so a player who somehow reaches
   * the title without it still gets sound.
   */
  private installSoundUnlock(): void {
    const unlock = (): void => {
      if (this.soundUnlocked) return;
      this.soundUnlocked = true;
      this.video.muted = false;
      this.video.volume = 0.7;
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  /**
   * Only the title screen carries the film; everything else pauses it to save power.
   *
   * Once it has finished the element stays mounted and black — it is the title's
   * backdrop from then on — but it is never played again.
   */
  private setVideoActive(active: boolean): void {
    // Fallback source for the paths that never went through the boot gate — `?play`,
    // `?fly`, a benchmark — which skip `loadIntroFilm` and so arrive here with no source.
    if (active && !this.video.src) this.video.src = introFilmSrc();
    this.video.classList.toggle('show', active);
    /*
     * The film sits on black rather than on the blurred live scene. It has to: the video
     * now fades in by opacity, and a transparent container would fade it up out of the
     * rig. Kept on for the About screen too, which is reached from the title without
     * touching the film.
     */
    if (active) this.root.classList.add('filmDark');
    if (active && !this.videoFinished) {
      void this.video.play().catch(() => { /* autoplay blocked until a gesture */ });
    } else {
      this.video.pause();
    }
  }

  get isShowing(): boolean {
    return this.root.classList.contains('show');
  }

  hide(): void {
    this.root.classList.remove('show');
    this.clearContent();
    this.setVideoActive(false);
    // Pause, shift report and defeat all want the blurred live scene behind them again.
    this.root.classList.remove('filmDark');
  }

  /** Remove screen content without touching the persistent video element. */
  private clearContent(): void {
    for (const child of Array.from(this.root.children)) {
      if (child !== this.video) child.remove();
    }
  }

  private render(html: string, wire: (root: HTMLElement) => void): HTMLDivElement {
    this.clearContent();
    // Exclusive to the gate; every other screen wants its scrim back.
    this.root.classList.remove('gate');
    const panel = document.createElement('div');
    panel.className = 'screen';
    panel.innerHTML = html;
    this.root.appendChild(panel);
    this.root.classList.add('show');
    wire(this.root);
    return panel;
  }

  private button(root: HTMLElement, id: string, fn: () => void): void {
    root.querySelector(`#${id}`)?.addEventListener('click', fn);
  }

  /**
   * The gate: one button, shown once, after loading finishes.
   *
   * It exists for a single technical reason with a creative consequence — a click is
   * the one gesture every browser accepts as permission to make noise, so this is what
   * guarantees the intro film is heard rather than watched in silence. It also unlocks
   * the game's own audio engine earlier than the first shift would.
   */
  showEnter(onEnter: () => void): void {
    const go = (): void => {
      /*
       * Everything about starting the film happens here, inside the gesture: unmute,
       * rewind to the first frame, then play. Calling play() synchronously in the
       * handler is also what keeps browsers happy about audio — a play() reached later,
       * down an async path, is one they are entitled to refuse.
       */
      this.soundUnlocked = true;
      this.video.muted = false;
      this.video.volume = 0.7;
      this.video.pause();
      this.video.currentTime = 0;
      void this.video.play().catch(() => { /* nothing more we can do */ });
      // The film opens alone; the menu joins it a couple of seconds later.
      this.titleEntrancePending = true;
      window.removeEventListener('keydown', onKey);
      onEnter();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
    };

    this.render(`
      <div class="actions">
        <button id="btnEnter" class="primary">Enter</button>
      </div>
    `, (root) => {
      this.button(root, 'btnEnter', go);
      window.addEventListener('keydown', onKey);
    });
    // The film stays dark and silent behind the gate until the gesture arrives.
    this.setVideoActive(false);
    /*
     * Pitch black, on Adam's direction: no scrim, no blurred rig, nothing to look at but
     * the one button. Set after `render`, which strips `gate` from whatever came before.
     */
    this.root.classList.add('filmDark', 'gate');
  }

  showTitle(
    hasProgress: boolean,
    shift: number,
    endless?: { unlocked: boolean; best: number },
  ): void {
    const panel = this.render(`
      <h1>Deepwater</h1>
      <!-- Adam's exact copy (2026-07-28): who you are and what matters, one breath,
           nothing a newcomer can misread. -->
      <p class="tagline">
        You are the last line of defense on a North Atlantic oil rig.<br/>
        Protect the convoys.
      </p>
      <div class="actions">
        ${hasProgress
          ? `<button id="btnContinue" class="primary">Continue — shift ${shift}</button>`
          : ''}
        <button id="btnNew" class="${hasProgress ? '' : 'primary'}">${hasProgress ? 'New campaign' : 'Begin shift 1'}</button>
        ${endless?.unlocked
          ? `<button id="btnEndless">Endless watch${endless.best > 0 ? ` — best ${endless.best}` : ''}</button>`
          : ''}
        <button id="btnAbout" class="ghost">About</button>
      </div>
    `, (root) => {
      this.button(root, 'btnContinue', this.cb.onContinue);
      this.button(root, 'btnNew', this.cb.onNewCampaign);
      this.button(root, 'btnEndless', this.cb.onEndless);
      this.button(root, 'btnAbout', () => this.showAbout(CREDITS));
    });
    if (this.titleEntrancePending) {
      this.titleEntrancePending = false;
      /*
       * The class holds the panel invisible and untouchable for the delay, then fades it
       * in; it is dropped on `animationend` so the buttons behave normally afterwards and
       * a re-render never inherits a half-finished entrance. `animationend` is guaranteed
       * here because the class is only added to a panel that is already in the document.
       */
      panel.classList.add('entrance');
      panel.addEventListener(
        'animationend',
        () => panel.classList.remove('entrance'),
        { once: true },
      );
    }
    this.setVideoActive(true);
  }

  showPause(settings: {
    volume: number; muted: boolean; invertScope: boolean; invertLook: boolean;
  }): void {
    this.render(`
      <h2>Paused</h2>
      <div class="reqPanel">
        <h3>Settings</h3>
        <div class="settingRow">
          <label for="setVolume">Master volume</label>
          <span>
            <input type="range" id="setVolume" min="0" max="100" step="1"
                   value="${Math.round(settings.volume * 100)}" />
            <span class="val mono" id="setVolumeVal">${Math.round(settings.volume * 100)}%</span>
          </span>
        </div>
        <div class="settingRow">
          <label for="setMute">Mute</label>
          <span><input type="checkbox" id="setMute" ${settings.muted ? 'checked' : ''} /></span>
        </div>
        <div class="settingRow">
          <label for="setInvertScope">Invert vertical aim — scope
            <em>Also on the INV button and the I key, without pausing</em></label>
          <span><input type="checkbox" id="setInvertScope" ${settings.invertScope ? 'checked' : ''} /></span>
        </div>
        <div class="settingRow">
          <label for="setInvertLook">Invert vertical look — wide view</label>
          <span><input type="checkbox" id="setInvertLook" ${settings.invertLook ? 'checked' : ''} /></span>
        </div>
      </div>
      <div class="actions">
        <button id="btnResume" class="primary">Resume</button>
        <button id="btnAbandon">Abandon shift</button>
      </div>
    `, (root) => {
      this.button(root, 'btnResume', this.cb.onResume);
      this.button(root, 'btnAbandon', this.cb.onAbandon);

      const vol = root.querySelector<HTMLInputElement>('#setVolume');
      const volVal = root.querySelector<HTMLElement>('#setVolumeVal');
      vol?.addEventListener('input', () => {
        const v = Number(vol.value) / 100;
        if (volVal) volVal.textContent = `${Math.round(v * 100)}%`;
        this.cb.onSetVolume(v);
      });

      root.querySelector<HTMLInputElement>('#setMute')?.addEventListener('change', (e) => {
        this.cb.onSetMuted((e.target as HTMLInputElement).checked);
      });
      root.querySelector<HTMLInputElement>('#setInvertScope')?.addEventListener('change', (e) => {
        this.cb.onSetInvert('scope', (e.target as HTMLInputElement).checked);
      });
      root.querySelector<HTMLInputElement>('#setInvertLook')?.addEventListener('change', (e) => {
        this.cb.onSetInvert('horizon', (e.target as HTMLInputElement).checked);
      });
    });
    this.setVideoActive(false);
  }

  /**
   * The shift report. Accuracy and rounds are in here because the gun is half the game —
   * a player who wants to get better needs to see whether they are actually hitting.
   */
  /**
   * The campaign is over and the player won.
   *
   * Deliberately quiet. The game's whole register is dread and endurance, and a
   * fanfare would be the one moment it stopped believing in itself — the sea going
   * flat and the sun coming up is the reward.
   */
  /**
   * A new build is waiting.
   *
   * A strip at the top of the screen, not a modal: the worker is deliberately built so
   * the update can wait indefinitely, so the prompt must never interrupt anything. If the
   * player ignores it forever the game keeps working on the build they started.
   */
  /**
   * About, and the licence ledger under it.
   *
   * This is the page a stranger lands on, so it answers their questions in the order they
   * ask them: what is this, what does it cost, what does it take from me, who made it.
   * The register stays the game's own — the sea is not sold here either.
   *
   * The credit list below the copy is generated from `docs/licenses.csv` at build time
   * rather than hand-maintained, because a credits list that drifts from the ledger is
   * worse than none: the ledger is the legal record and this is only its public face.
   * Nothing currently shipped demands on-screen attribution (it is all CC0, public
   * domain, OFL, MIT/Apache or owner-supplied), but the section stays regardless — the
   * first CC-BY asset we take makes it a condition of use, and by then nobody will
   * remember to add it back.
   */
  showAbout(entries: ReadonlyArray<{
    name: string; category: string; author: string; license: string; url: string;
  }>): void {
    const byCategory = new Map<string, typeof entries[number][]>();
    for (const e of entries) {
      const list = byCategory.get(e.category) ?? [];
      list.push(e);
      byCategory.set(e.category, list);
    }

    const sections = [...byCategory.entries()].map(([category, list]) => `
      <h3>${category}</h3>
      <div class="rows">
        ${list.map((e) => `
          <div class="row">
            <span class="k">${e.name}</span>
            <span class="v">${e.author} — ${e.license}</span>
          </div>`).join('')}
      </div>
    `).join('');

    this.render(`
      <h1>About</h1>
      <h2>Deepwater</h2>
      <p class="about">
        Deepwater is free to play: no ads, no account, no purchases, nothing held back to
        sell you later. If you paid money for this somewhere, someone else charged you
        for it.
      </p>
      <p class="about">
        It runs entirely in your browser — nothing is installed, and after the first visit
        it plays with no connection at all. There are no cookies and nothing that
        identifies you or your device, and offline it sends nothing at all.
      </p>
      <p class="about">
        Made by Adam. Built in under 72 hours with Opus 5, Fable, Codex 5.6 using
        Babylon.js The ocean, the sky, the rig, the vessels and the creatures are all
        generated in code; the surface detail comes from CC0 photogrammetry, credited
        below. The source is open:
        <a class="link" href="https://github.com/adamplg/DEEPWATER" target="_blank" rel="noopener noreferrer">github.com/adamplg/DEEPWATER</a>.
      </p>
      <p class="about">
        <a class="link" href="https://edmundgrey.com" target="_blank" rel="noopener noreferrer">edmundgrey.com</a>
      </p>
      <h2 class="ledgerHead">Made with</h2>
      <p class="about quiet">
        Everything Deepwater is built from, and the licence it came under. The full record,
        with each licence proof archived alongside it, ships in the source repository.
      </p>
      ${sections}
      <div class="actions">
        <button id="btnBack" class="primary">Back</button>
      </div>
    `, (root) => {
      this.button(root, 'btnBack', this.cb.onAbandon);
    });
  }

  showUpdatePrompt(apply: () => void): void {
    if (document.getElementById('updateStrip')) return;
    const strip = document.createElement('div');
    strip.id = 'updateStrip';
    strip.innerHTML = '<span>A new version is ready.</span>'
      + '<button id="btnUpdate" type="button">Update</button>'
      + '<button id="btnUpdateLater" type="button" class="ghost">Later</button>';
    document.body.appendChild(strip);
    strip.querySelector('#btnUpdate')?.addEventListener('click', apply);
    strip.querySelector('#btnUpdateLater')?.addEventListener('click', () => strip.remove());
  }

  showVictory(resources: Resources, stats: {
    shiftsCompleted: number; hostilesDestroyed: number; moneyEarned: number;
  }): void {
    this.render(`
      <h1>Dawn</h1>
      <h2>It went under, and the sea closed over it.</h2>
      <p class="lede">
        Eight watches. The platform is still standing, and the crude is still moving.
        Nobody ashore will ever be told what came up out of the water.
      </p>
      <div class="rows">
        <div class="row"><span class="k">Shifts survived</span><span class="v mono">${stats.shiftsCompleted}</span></div>
        <div class="row"><span class="k">Hostiles destroyed</span><span class="v mono">${stats.hostilesDestroyed}</span></div>
        <div class="row"><span class="k">Total earnings</span><span class="v mono good">$${Math.round(stats.moneyEarned).toLocaleString()}</span></div>
        <div class="row"><span class="k">Rig integrity</span><span class="v mono ${resources.integrity < 50 ? 'bad' : ''}">${Math.round(resources.integrity)}%</span></div>
        <div class="row"><span class="k">Crew remaining</span><span class="v mono">${Math.round(resources.crew)}</span></div>
      </div>
      <div class="actions">
        <button id="btnEndless" class="primary">Endless watch</button>
        <button id="btnTitle2">Title</button>
      </div>
    `, (root) => {
      this.button(root, 'btnEndless', this.cb.onEndless);
      this.button(root, 'btnTitle2', this.cb.onAbandon);
    });
  }

  showShiftReport(shift: ShiftProgress, resources: Resources, isLastShift: boolean): void {
    const accuracy = shift.roundsFired > 0
      ? `${((shift.roundsHit / shift.roundsFired) * 100).toFixed(1)}%`
      : '—';
    const lost = shift.arrivalsLost;

    this.render(`
      <h1>Shift ${shift.index}</h1>
      <h2>${lost === 0 ? 'Convoy intact' : `${lost} vessel${lost > 1 ? 's' : ''} lost`}</h2>
      <div class="rows">
        <div class="row"><span class="k">Crude delivered</span><span class="v mono">${Math.round(shift.oilDelivered).toLocaleString()} bbl</span></div>
        <div class="row"><span class="k">Earnings</span><span class="v mono good">$${Math.round(shift.earned).toLocaleString()}</span></div>
        <div class="row"><span class="k">Vessels safe</span><span class="v mono">${shift.arrivalsSafe} / ${shift.arrivalsScheduled}</span></div>
        <div class="row"><span class="k">Vessels lost</span><span class="v mono ${lost > 0 ? 'bad' : ''}">${lost}</span></div>
        <div class="row"><span class="k">Hostiles destroyed</span><span class="v mono">${shift.hostilesDestroyed}</span></div>
        <div class="row"><span class="k">Rounds fired</span><span class="v mono">${shift.roundsFired.toLocaleString()}</span></div>
        <div class="row"><span class="k">Accuracy</span><span class="v mono">${accuracy}</span></div>
        <div class="row"><span class="k">Rig integrity</span><span class="v mono ${resources.integrity < 50 ? 'bad' : ''}">${Math.round(resources.integrity)}%</span></div>
        <div class="row"><span class="k">Funds</span><span class="v mono">$${Math.round(resources.money).toLocaleString()}</span></div>
      </div>
      <div class="actions">
        ${isLastShift
          ? '<button id="btnTitle" class="primary">Return to title</button>'
          : '<button id="btnReq" class="primary">Requisition</button>'}
        <button id="btnTitle2">Title</button>
      </div>
      ${isLastShift ? '<p class="tagline">That is every shift built so far. More are coming.</p>' : ''}
    `, (root) => {
      this.button(root, 'btnReq', this.cb.onRequisition);
      this.button(root, 'btnTitle', this.cb.onAbandon);
      this.button(root, 'btnTitle2', this.cb.onAbandon);
    });
    this.setVideoActive(false);
  }

  /**
   * The requisition screen: where money becomes capability.
   *
   * Two panels, per plan A5. Upgrades are instant and permanent. Manifest items are
   * *cargo* — they ride in on the next shift's supply vessel and only apply if she
   * survives, which is what makes defending her matter rather than being a chore.
   */
  showRequisition(view: RequisitionView): void {
    const owned = new Set(view.upgrades);
    const counts = new Map<string, number>();
    for (const id of view.pendingManifest) counts.set(id, (counts.get(id) ?? 0) + 1);

    const money = Math.round(view.resources.money);

    const manifestRows = MANIFEST_ITEMS.map((item) => {
      const n = counts.get(item.id) ?? 0;
      return `
        <div class="reqRow" data-manifest="${item.id}">
          <span class="reqName">${item.label}</span>
          <span class="reqCost mono">$${item.cost.toLocaleString()}</span>
          <span class="reqQty">
            <button class="reqBtn" data-delta="-1" ${n === 0 ? 'disabled' : ''}>−</button>
            <span class="mono reqCount">${n}</span>
            <button class="reqBtn" data-delta="1" ${money < item.cost ? 'disabled' : ''}>+</button>
          </span>
        </div>`;
    }).join('');

    const upgradeRows = UPGRADES.map((u) => {
      const has = owned.has(u.id);
      return `
        <div class="reqRow ${has ? 'owned' : ''}" data-upgrade="${u.id}">
          <span class="reqName">${u.label}<em>${u.description}</em></span>
          <span class="reqCost mono">${has ? 'fitted' : `$${u.cost.toLocaleString()}`}</span>
          <span class="reqQty">
            <button class="reqBtn buy" ${has || money < u.cost ? 'disabled' : ''}>Buy</button>
          </span>
        </div>`;
    }).join('');

    this.render(`
      <h1>Requisition</h1>
      <h2>Before shift ${view.nextShift}</h2>
      <div class="reqFunds">Available <span class="mono" id="reqFunds">$${money.toLocaleString()}</span></div>

      <div class="reqPanel">
        <h3>Supply manifest <em>arrives with the next supply vessel — she must survive</em></h3>
        ${manifestRows}
      </div>

      <div class="reqPanel">
        <h3>Rig upgrades <em>instant and permanent</em></h3>
        ${upgradeRows}
      </div>

      <div class="actions">
        <button id="btnBegin" class="primary">Begin shift ${view.nextShift}</button>
      </div>
    `, (root) => {
      this.button(root, 'btnBegin', this.cb.onNextShift);

      root.querySelectorAll<HTMLElement>('[data-manifest]').forEach((row) => {
        const id = row.dataset['manifest'] as string;
        row.querySelectorAll<HTMLButtonElement>('.reqBtn').forEach((btn) => {
          btn.addEventListener('click', () => {
            const delta = Number(btn.dataset['delta']);
            this.cb.onOrderManifest(id, delta);
            this.refreshRequisition();
          });
        });
      });

      root.querySelectorAll<HTMLElement>('[data-upgrade]').forEach((row) => {
        const id = row.dataset['upgrade'] as UpgradeId;
        row.querySelector<HTMLButtonElement>('.buy')?.addEventListener('click', () => {
          if (this.cb.onBuyUpgrade(id)) this.refreshRequisition();
        });
      });
    });
    this.setVideoActive(false);
  }

  /**
   * The one-time explanation of what a manifest order actually is.
   *
   * Ordered goods are cargo, not a purchase that lands in the locker: they ride in on the
   * next shift's supply vessel and only apply once she is safely in. That is deliberate —
   * it is what makes defending a supply run matter rather than being a chore — but the
   * only place it was ever said was one line of italics on a screen the player has already
   * left by the time it matters. Adam bought torpedoes, started the next shift, found the
   * same count on the weapon card and reasonably concluded the shop was broken.
   *
   * Shown once, on the way out of the first requisition, and only to a player who actually
   * ordered something: a notice about purchases you did not make is noise, and the lesson
   * keeps until there is a delivery to attach it to.
   *
   * Set in `about` rather than `lede`, which is what every other screen's prose uses: this
   * is an explanation at paragraph length, and the overlay's blanket uppercase is close to
   * unreadable at that length. It also gets the About page's measure and its spacing
   * between paragraphs, both of which `lede` lacks — it carries no style rule at all.
   */
  showManifestNotice(nextShift: number, onDismiss: () => void): void {
    this.render(`
      <h1>Manifest away</h1>
      <h2>Your order is cargo now</h2>
      <p class="about">
        What you bought is not in the locker — it is on a boat. The supply vessel brings it
        out to you during shift ${nextShift}, and it is yours the moment she is safely in.
      </p>
      <p class="about">
        Which means she is worth defending. If she goes down, your order goes down with her.
      </p>
      <div class="actions">
        <button id="btnUnderstood" class="primary">Understood</button>
      </div>
    `, (root) => {
      this.button(root, 'btnUnderstood', onDismiss);
    });
    this.setVideoActive(false);
  }

  /**
   * The shift skipper — Adam's, for testing the deployed build.
   *
   * Production has no console and a phone has no keyboard, so the only honest way to see
   * a late shift was to play every one before it. Reached by the four-control hold in the HUD
   * (`wireCheatGesture`), which is deliberately awkward enough that no player finds it by
   * accident, and every shift is listed whether or not the campaign has reached it.
   *
   * It is a cheat and says so. Nothing here pretends to be part of the game: the heading
   * names it, and the note says what it will do to the save, because it does persist —
   * jumping to shift six leaves the campaign on shift six, which is what makes it useful
   * for coming back to the same place twice.
   */
  showShiftSkip(current: number, onPick: (shiftIndex: number) => void, onResume: () => void): void {
    const cells = SHIFTS.map((s) => `
      <button class="skipCell${s.index === current ? ' here' : ''}" data-shift="${s.index}">
        <span class="skipNum mono">${s.index}</span>
        <span class="skipName">${s.name}</span>
      </button>`).join('');

    this.render(`
      <h1>Shift skip</h1>
      <h2>Testing only</h2>
      <p class="about">
        Starts any shift with a full magazine, both racks loaded and the rig at full
        integrity. Your save moves with you.
      </p>
      <div class="skipGrid">${cells}</div>
      <div class="actions">
        <button id="btnEndlessSkip">Endless, watch 1</button>
        <button id="btnSkipBack" class="primary">Back to the watch</button>
      </div>
    `, (root) => {
      this.button(root, 'btnSkipBack', onResume);
      this.button(root, 'btnEndlessSkip', () => onPick(101));
      root.querySelectorAll<HTMLElement>('[data-shift]').forEach((cell) => {
        cell.addEventListener('click', () => onPick(Number(cell.dataset['shift'])));
      });
    });
    this.setVideoActive(false);
  }

  /** Re-render the requisition screen after a purchase, preserving scroll position. */
  private refreshRequisition(): void {
    if (!this.requisitionView) return;
    const panel = this.root.querySelector('.screen');
    const scroll = panel?.scrollTop ?? 0;
    this.showRequisition(this.requisitionView());
    const next = this.root.querySelector('.screen');
    if (next) next.scrollTop = scroll;
  }

  /** Supplied by the game so the screen can rebuild itself from live state. */
  requisitionView: (() => RequisitionView) | null = null;

  showDefeat(shift: ShiftProgress): void {
    this.render(`
      <h1>Rig lost</h1>
      <h2>Shift ${shift.index}</h2>
      <p class="tagline">
        The platform could not be held. Evacuation order given.
      </p>
      <div class="actions">
        <button id="btnRetry" class="primary">Retry shift</button>
        <button id="btnTitle">Title</button>
      </div>
    `, (root) => {
      this.button(root, 'btnRetry', this.cb.onRetryShift);
      this.button(root, 'btnTitle', this.cb.onAbandon);
    });
    this.setVideoActive(false);
  }

  dispose(): void {
    this.video.pause();
    this.video.removeAttribute('src');
    // The blob holds the whole film in memory until this is called.
    if (this.filmUrl) URL.revokeObjectURL(this.filmUrl);
    this.filmUrl = null;
    this.root.remove();
  }
}
